import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

export default function Shop() {
  const { category } = useParams();
  const [sp, setSp] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({});
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => { api.get("/facets").then((r) => setFacets(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    ["q", "subcategory", "size", "color", "fabric", "occasion", "age", "sort", "featured", "min_price", "max_price"].forEach((k) => {
      const v = sp.get(k); if (v) params.set(k, v);
    });
    api.get(`/products?${params.toString()}`).then((r) => { setProducts(r.data.items); setTotal(r.data.total); }).finally(() => setLoading(false));
  }, [category, sp]);

  const setFilter = (k, v) => { const n = new URLSearchParams(sp); v ? n.set(k, v) : n.delete(k); setSp(n); };
  const clearAll = () => setSp({});

  const Filters = () => (
    <div className="space-y-6" data-testid="filters">
      {category === "women" && (
        <FilterGroup title="Category" options={facets.subcategories_women} active={sp.get("subcategory")} onSelect={(v) => setFilter("subcategory", v)} />
      )}
      {category === "kids" && (
        <>
          <FilterGroup title="Category" options={facets.subcategories_kids} active={sp.get("subcategory")} onSelect={(v) => setFilter("subcategory", v)} />
          <FilterGroup title="Age" options={facets.ages} active={sp.get("age")} onSelect={(v) => setFilter("age", v)} />
        </>
      )}
      <FilterGroup title="Fabric" options={facets.fabrics} active={sp.get("fabric")} onSelect={(v) => setFilter("fabric", v)} />
      <FilterGroup title="Occasion" options={facets.occasions} active={sp.get("occasion")} onSelect={(v) => setFilter("occasion", v)} />
      <div>
        <div className="eyebrow mb-3">Color</div>
        <div className="flex flex-wrap gap-2">
          {facets.colors?.map((c) => (
            <button key={c} onClick={() => setFilter("color", sp.get("color") === c ? "" : c)}
              className={`text-xs px-3 py-1.5 border ${sp.get("color") === c ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#D4AF37]/30"}`}
              data-testid={`color-${c}`}>{c}</button>
          ))}
        </div>
      </div>
      <FilterGroup title="Size" options={category === "kids" ? facets.kid_sizes : facets.sizes} active={sp.get("size")} onSelect={(v) => setFilter("size", v)} />
      <button onClick={clearAll} className="text-xs tracking-widest uppercase text-[#0B1F3A] underline" data-testid="clear-filters">Clear all</button>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10">
      <div className="mb-4 md:mb-10">
        <div className="eyebrow mb-1.5 md:mb-2">{category ? category.toUpperCase() : "All"}</div>
        <h1 className="font-serif text-2xl md:text-4xl text-[#0B1F3A]">
          {sp.get("q") ? `"${sp.get("q")}"` : (category === "women" ? "Women's Edit" : category === "kids" ? "Kids' Edit" : "Everything")}
        </h1>
        <div className="text-xs md:text-sm text-[#1E293B]/60 mt-1">{total} pieces</div>
      </div>

      <div className="flex gap-8">
        <aside className="hidden md:block w-60 shrink-0"><Filters /></aside>
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-center gap-2 mb-5 md:mb-6 sticky top-[60px] md:static bg-[#FCE4EC]/95 md:bg-transparent backdrop-blur md:backdrop-blur-none py-2 z-20 -mx-4 px-4 md:mx-0 md:px-0">
            <button onClick={() => setShowFilters(true)} className="md:hidden flex-1 flex items-center justify-center gap-2 text-xs tracking-widest uppercase border px-3 py-2.5 bg-white" style={{ borderColor: "rgba(212,175,55,0.4)" }} data-testid="show-filters">
              <SlidersHorizontal size={14} /> Filter
            </button>
            <select value={sp.get("sort") || "new"} onChange={(e) => setFilter("sort", e.target.value)}
              className="flex-1 md:flex-none md:ml-auto bg-white border px-3 py-2.5 text-xs md:text-sm tracking-widest uppercase" style={{ borderColor: "rgba(212,175,55,0.4)" }} data-testid="sort-select">
              <option value="new">Newest</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
            </select>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">{[...Array(6)].map((_, i) => <div key={i} className="aspect-[3/4] bg-white/60 animate-pulse" />)}</div>
          ) : products.length === 0 ? (
            <div className="text-center py-24 text-[#1E293B]/60">
              <div className="font-serif text-2xl text-[#0B1F3A] mb-2">Nothing matches — yet.</div>
              <button onClick={clearAll} className="underline text-sm tracking-widest uppercase">Clear filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6">{products.map((p) => <ProductCard key={p.id} p={p} />)}</div>
          )}
        </div>
      </div>

      {showFilters && (
        <div className="fixed inset-0 z-50 bg-black/40 md:hidden" onClick={() => setShowFilters(false)}>
          <div className="w-80 h-full bg-[#FCE4EC] p-6 overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between mb-6"><div className="font-serif text-xl">Filters</div><button onClick={() => setShowFilters(false)} data-testid="close-filters"><X /></button></div>
            <Filters />
          </div>
        </div>
      )}
    </div>
  );
}

function FilterGroup({ title, options, active, onSelect }) {
  if (!options) return null;
  return (
    <div>
      <div className="eyebrow mb-3">{title}</div>
      <div className="space-y-1.5">
        {options.map((o) => (
          <button key={o} onClick={() => onSelect(active === o ? "" : o)}
            className={`block w-full text-left text-sm py-1 ${active === o ? "text-[#D4AF37] font-semibold" : "text-[#1E293B]/80 hover:text-[#0B1F3A]"}`}
            data-testid={`filter-${title}-${o}`}>{o}</button>
        ))}
      </div>
    </div>
  );
}
